import { useState } from "react";

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
  DrawerHeader,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { FormCreateFinanceEntry } from "./form-create-finance-entry";
import { FormUpdateFinanceEntry } from "./form-update-finance-entry";
import { FormExchangeFinanceEntry } from "./form-exchange-finance-entry";
import { DocumentsFinanceEntries } from "./documents-finance-entries";

type FormFinanceEntryProps = {
  /** The element that opens the drawer. Omitted when the caller controls it. */
  children?: React.ReactNode;
  id?: string;
  isEdit?: boolean;
  disable?: boolean;
  patientId?: string;
  /** Controlled mode: one drawer owned by a list, opened for the chosen row. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const tabs = [
  {
    label: "Lançamentos",
    value: "entry",
  },
  {
    label: "Dar baixa",
    value: "exchange",
  },
  {
    label: "Anexos",
    value: "attachment",
  },
];

export function FormFinanceEntry({
  children,
  isEdit = false,
  id,
  disable,
  patientId,
  open: openProp,
  onOpenChange,
}: FormFinanceEntryProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(isEdit);
  const open = openProp ?? uncontrolledOpen;

  function setOpen(next: boolean) {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }

  return (
    <Drawer open={disable ? false : open} onOpenChange={setOpen}>
      {children ? <DrawerTrigger asChild>{children}</DrawerTrigger> : null}
      <DrawerContent className="h-[94vh]">
        <Tabs defaultValue="entry">
          <DrawerHeader className="flex-none flex justify-between items-center mt-5">
            <div className="space-y-1">
              <DrawerTitle className="text-teal-700 font-bold">
                {id ? "Editar" : "Novo"} lançamento
              </DrawerTitle>
              <DrawerDescription>
                Preencha os campos para {id ? "editar" : "criar"} um lançamento
                financeiro.
              </DrawerDescription>
            </div>
            <TabsList>
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  disabled={tab.value !== "entry" && !id}
                  className="data-[state=active]:bg-teal-700 data-[state=active]:text-white"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </DrawerHeader>
          <TabsContent value="entry">
            {id ? (
              <FormUpdateFinanceEntry
                handleClose={() => setOpen(false)}
                id={id}
                patientId={patientId}
              />
            ) : (
              <FormCreateFinanceEntry patientId={patientId} handleClose={() => setOpen(false)} />
            )}
          </TabsContent>
          <TabsContent value="exchange">
            {id && (
              <FormExchangeFinanceEntry
                id={id}
                handleClose={() => setOpen(false)}
              />
            )}
          </TabsContent>
          <TabsContent value="attachment">
            {id && <DocumentsFinanceEntries id={id} />}
          </TabsContent>
        </Tabs>
      </DrawerContent>
    </Drawer>
  );
}
